// Load your day (no calendar sync): text, voice or a photo in, a day of visits out.
import React, { useEffect, useRef, useState } from 'react';
import { clockLabel, DEFAULT_VISIT_MIN } from './visitsDay';
import { Eq, Mic, Photo } from './icons';

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// "09:00" / "9:30 AM" -> minutes from midnight, or null.
function toMin(time) {
  const m = String(time || '')
    .trim()
    .match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])?m?$/i);
  if (!m) return null;
  let h = Number(m[1]);
  const mi = Number(m[2] || 0);
  const ap = m[3] && m[3].toLowerCase();
  if (ap === 'p' && h < 12) h += 12;
  if (ap === 'a' && h === 12) h = 0;
  return h < 24 && mi < 60 ? h * 60 + mi : null;
}

// Untimed visits follow the timed ones, back to back.
function toItems(visits) {
  const items = (visits || [])
    .filter((v) => (v.patientName || '').trim())
    .map((v) => ({
      name: v.patientName.trim(),
      min: toMin(v.time),
      dur:
        Number(v.durationMin) > 0 ? Number(v.durationMin) : DEFAULT_VISIT_MIN,
    }));
  let cursor = Math.max(
    8 * 60,
    ...items.filter((it) => it.min !== null).map((it) => it.min + it.dur),
  );
  return items
    .map((it) => {
      if (it.min !== null) return it;
      const placed = { ...it, min: cursor };
      cursor += it.dur;
      return placed;
    })
    .sort((a, b) => a.min - b.min);
}

const LoadDaySheet = ({ day, onParse, onCreate, onClose }) => {
  const [step, setStep] = useState('write'); // write | think | review
  const [text, setText] = useState('');
  const [img, setImg] = useState(null); // { url, name }
  const [items, setItems] = useState([]);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const recRef = useRef(null);

  // Closing the sheet mid-recording unmounts it: release the mic and skip the parse.
  useEffect(
    () => () => {
      const rec = recRef.current;
      if (rec && rec.state !== 'inactive') {
        rec.onstop = () => rec.stream.getTracks().forEach((t) => t.stop());
        rec.stop();
      }
    },
    [],
  );

  const parse = async (payload) => {
    setStep('think');
    setError('');
    try {
      const rsp = await onParse(payload);
      if (!rsp || rsp.error) throw new Error(rsp?.error || 'parse failed');
      setItems(toItems(rsp.visits));
      setStep('review');
    } catch (e) {
      setError('Could not read that — try again.');
      setStep('write');
    }
  };

  const toggleMic = async () => {
    if (listening) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const parts = [];
      const rec = new MediaRecorder(stream);
      recRef.current = rec;
      rec.ondataavailable = (e) => e.data.size > 0 && parts.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        const blob = new Blob(parts, { type: rec.mimeType || 'audio/webm' });
        if (blob.size > 0) parse({ audio: await blobToDataURL(blob) });
      };
      rec.start();
      setListening(true);
    } catch (e) {
      setError('Microphone unavailable.');
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setImg({ url: await blobToDataURL(file), name: file.name });
  };

  const add = async () => {
    setStep('think');
    await onCreate(items);
  };

  const dayLong = day.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const head = (
    <div className="vs-sheet-head">
      <div>
        <h3>Load your day</h3>
        <p className="vs-sub">{dayLong}</p>
      </div>
      <span className="vs-sp" />
      <button
        type="button"
        className="vs-sheetx"
        aria-label="Close"
        onClick={onClose}
      >
        ×
      </button>
    </div>
  );

  let content;
  if (step === 'think') {
    content = (
      <>
        <div className="vs-bulkthink">
          <Eq />
          {img && !text.trim() ? 'Reading your photo…' : 'Reading your day…'}
        </div>
        <div className="vs-bar vs-indet">
          <i />
        </div>
      </>
    );
  } else if (step === 'review') {
    content = (
      <>
        {items.length ? (
          <div className="vs-bulklist">
            {items.map((it, i) => (
              <div key={`${it.name}-${i}`} className="vs-bulkrow">
                <span className="vs-bt">{clockLabel(it.min)}</span>
                <span className="vs-bn">{it.name}</span>
                <span className="vs-bs">{`${it.dur} min`}</span>
                <button
                  type="button"
                  className="vs-bx"
                  aria-label={`Leave out ${it.name}`}
                  onClick={() => setItems(items.filter((_, k) => k !== i))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="vs-pnone">Nothing left to add.</p>
        )}
        <p className="vs-bulknote">
          They land in edit mode, so you can drag any of them to fix a time.
        </p>
        <div className="vs-bulkacts">
          <button
            type="button"
            className="vs-linkbtn"
            onClick={() => setStep('write')}
          >
            Back to text
          </button>
          <span className="vs-sp" />
          <button
            type="button"
            className="vs-bulkgo"
            disabled={!items.length}
            onClick={add}
          >
            {`Add ${items.length} visit${items.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </>
    );
  } else {
    const ready = !!(text.trim() || img);
    content = (
      <>
        <textarea
          className={`vs-bulkta${listening ? ' vs-listening' : ''}`}
          autoFocus
          placeholder="Rattle off your day — “Annie at 9, Jimmy 9:30, Marcus and Elena at 10…”"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {img && (
          <div className="vs-bulkimg">
            <img src={img.url} alt="" />
            <span>{img.name}</span>
            <button
              type="button"
              className="vs-bx"
              aria-label="Remove the photo"
              onClick={() => setImg(null)}
            >
              ×
            </button>
          </div>
        )}
        {error && <p className="vs-delwarn">{error}</p>}
        <div className="vs-bulkacts">
          <button
            type="button"
            className="vs-bulkgo vs-grow"
            disabled={!ready || listening}
            onClick={() =>
              parse(text.trim() ? { text: text.trim() } : { image: img.url })
            }
          >
            Load
          </button>
          <button
            type="button"
            className={`vs-bulkmic${listening ? ' vs-on' : ''}`}
            onClick={toggleMic}
          >
            {listening ? (
              <>
                <Eq small />
                Stop
              </>
            ) : (
              <>
                <Mic />
                Say it
              </>
            )}
          </button>
          <label
            className="vs-bulkpic"
            title="Add a photo or screenshot of your schedule"
          >
            <Photo />
            <input
              type="file"
              accept="image/*"
              aria-label="Add a photo of your schedule"
              onChange={onFile}
            />
          </label>
        </div>
        <p className="vs-bulknote">
          Type it, say it, or snap your paper schedule or the front desk’s
          screen.
        </p>
      </>
    );
  }

  return (
    <div
      className="vs-sheet-wrap"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="vs-sheet">
        {head}
        {content}
      </div>
    </div>
  );
};

export default LoadDaySheet;
